import { CloseOutlined, PlusOutlined } from "@ant-design/icons"
import { Button, ButtonProps } from "components/Button/Button"
import { Select, type SelectProps } from "components/Select/Select"
import { Tag, TagProps } from "components/Tag/Tag"
import { Tooltip } from "components/Tooltip/Tooltip"
import {
	ComponentPropsWithRef,
	ReactNode,
	useCallback,
	useMemo,
	useState,
} from "react"
import { cn } from "styles/cn"

export function TagSelect({
	className,
	disabled = false,
	value = [],
	onChange,
	onSearch,
	onSearchClose,
	renderTagContent,
	isTagPending,

	tagProps,
	selectProps,
	buttonProps,
	...props
}: TagSelectProps) {
	const [isAdding, setIsAdding] = useState(false)
	const addAriaLabel = buttonProps?.["aria-label"]
	const options = useMemo(() => selectProps?.options ?? [], [selectProps])
	const optionsMap = useMemo(() => {
		const allFlatOptions = flattenOptions(options)
		const map = new Map<string, OptionType>()
		for (const opt of allFlatOptions) {
			if (opt.value !== undefined && opt.value !== null) {
				map.set(String(opt.value), opt)
			}
		}
		return map
	}, [options])
	const availableOptions = useMemo(() => {
		const selectedSet = new Set(value)
		const filtered: NonNullable<SelectProps["options"]> = []

		for (const item of options) {
			if ("options" in item && Array.isArray(item.options)) {
				const unselectedGroupOptions = item.options.filter(
					(opt) =>
						opt.value !== undefined &&
						opt.value !== null &&
						!selectedSet.has(String(opt.value)),
				)
				if (unselectedGroupOptions.length > 0) {
					filtered.push({
						...item,
						options: unselectedGroupOptions,
					})
				}
			} else if (
				item.value !== undefined &&
				item.value !== null &&
				!selectedSet.has(String(item.value))
			) {
				filtered.push(item)
			}
		}

		return filtered
	}, [options, value])
	const isAsyncSearch = !!onSearch
	const canAddMore = !disabled && (isAsyncSearch || availableOptions.length > 0)
	const handleValueChange = useCallback(
		(newValues: string[]) => {
			const selectedOpts = newValues.map(
				(v) => optionsMap.get(v) || { label: v, value: v },
			)
			onChange?.(newValues, selectedOpts)
		},
		[optionsMap, onChange],
	)
	const handleRemoveTag = useCallback(
		(valueToRemove: string) => {
			if (disabled) {
				return
			}
			const nextValues = value.filter((v) => v !== valueToRemove)
			handleValueChange(nextValues)
		},
		[disabled, value, handleValueChange],
	)
	const handleSelectOption = useCallback(
		(selectedVal: string) => {
			if (selectedVal === undefined || selectedVal === null) {
				return
			}
			const nextValues = [...value, String(selectedVal)]
			handleValueChange(nextValues)
			setIsAdding(false)
			onSearchClose?.()
		},
		[value, handleValueChange, onSearchClose],
	)

	return (
		<div
			{...props}
			className={cn("flex flex-wrap items-center gap-1", className)}
		>
			{value.map((val) => {
				const opt = optionsMap.get(val)
				const label = opt ? opt.label : val
				const textForColor = typeof label === "string" ? label : val
				const customColor = (opt as { color?: string })?.color
				const tagColor = customColor || computeColorFromText(textForColor)
				const isPending = isTagPending?.(val) ?? false

				return (
					<Tag
						{...tagProps}
						key={val}
						variant="filled"
						color={tagColor}
						className={cn(
							"mr-0",
							tagProps?.className,
							isPending && "opacity-50",
						)}
						closeIcon={
							!disabled && !isPending ? (
								<CloseOutlined
									tabIndex={0}
									role="button"
									aria-label={`Remove ${label}`}
									className="focus-outline rounded-xl outline-offset-1!"
									onKeyDown={(e) => {
										if (e.key === "Enter" || e.key === " ") {
											e.preventDefault()
											handleRemoveTag(val)
										}
									}}
								/>
							) : undefined
						}
						onClose={() => handleRemoveTag(val)}
					>
						{renderTagContent ? renderTagContent(opt, val) : label}
					</Tag>
				)
			})}

			{canAddMore &&
				(isAdding ? (
					<Select
						{...selectProps}
						autoFocus
						showSearch={{
							onSearch,
							filterOption: isAsyncSearch ? false : undefined,
						}}
						defaultOpen
						size="small"
						className="h-5.5! min-w-36"
						variant="underlined"
						placeholder={selectProps?.placeholder ?? "Search..."}
						options={availableOptions}
						popupMatchSelectWidth={false}
						onChange={handleSelectOption}
						onOpenChange={(isOpen) => {
							if (!isOpen) {
								setIsAdding(false)
								onSearchClose?.()
							}
						}}
					/>
				) : (
					<Tooltip title={addAriaLabel}>
						<Button
							{...buttonProps}
							size="small"
							type="dashed"
							shape="round"
							disabled={disabled}
							className={cn("h-auto text-sm", buttonProps?.className)}
							icon={<PlusOutlined aria-hidden />}
							aria-label={addAriaLabel}
							onClick={() => setIsAdding(true)}
						>
							{addAriaLabel ? undefined : (buttonProps?.children ?? "Add")}
						</Button>
					</Tooltip>
				))}
		</div>
	)
}

function flattenOptions(options: SelectProps["options"] = []) {
	const flat: OptionType[] = []
	for (const opt of options) {
		if ("options" in opt && Array.isArray(opt.options)) {
			flat.push(...opt.options)
		} else {
			flat.push(opt)
		}
	}
	return flat
}

const AVAILABLE_COLORS = [
	"blue",
	"red",
	"orange",
	"volcano",
	"geekblue",
] as const

function computeColorFromText(text: string): string {
	if (!text) {
		return "default"
	}
	let hash = 0
	for (let i = 0; i < text.length; i++) {
		hash = (hash << 5) - hash + text.charCodeAt(i)
		hash |= 0
	}
	const index = Math.abs(hash) % AVAILABLE_COLORS.length
	return AVAILABLE_COLORS[index]
}

export interface TagSelectProps extends Omit<
	ComponentPropsWithRef<"div">,
	"onChange" | "defaultValue"
> {
	value?: string[]
	onChange?: (
		values: string[],
		selectedOptions: NonNullable<SelectProps["options"]>,
	) => void
	disabled?: boolean
	onSearch?: (query: string) => void
	onSearchClose?: () => void
	renderTagContent?: (
		option: OptionType | undefined,
		value: string,
	) => ReactNode
	isTagPending?: (value: string) => boolean

	tagProps?: TagProps
	selectProps?: SelectProps
	buttonProps?: ButtonProps
}

export type OptionType = NonNullable<SelectProps["options"]>[number]
